import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import EventIcon from '@mui/icons-material/Event';
import SpeedIcon from '@mui/icons-material/Speed';
import BuildIcon from '@mui/icons-material/Build';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PrintIcon from '@mui/icons-material/Print';
import { notificationService } from '../api/api';
import { useAuth } from '../auth/AuthContext';
import { formatPortalDate } from '../utils/dateFormat';

function formatDate(value) {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return formatPortalDate(date);
}

function formatKm(value) {
  if (value === null || value === undefined || value === '') return null;
  return `${Number(value).toLocaleString()} km`;
}

function typeMeta(type) {
  if (type === 'low_stock_part') {
    return {
      label: 'Purchase Part',
      icon: <Inventory2Icon fontSize="small" />,
      color: 'warning',
      accent: '#22c55e',
      bg: 'linear-gradient(135deg, #f5fdf7 0%, #f8fff9 100%)',
      border: '#86efac',
      chipBg: '#e5f9eb',
      chipColor: '#166534',
      actionBg: '#15803d',
      actionText: 'Open Inventory',
    };
  }
  if (type === 'scheduled_maintenance') return { label: 'Maintenance', icon: <BuildIcon fontSize="small" />, color: 'warning' };
  if (type === 'glove_box_expiry') {
    return {
      label: 'Glove Box',
      icon: <ReceiptIcon fontSize="small" />,
      color: 'info',
      accent: '#2563eb',
      bg: 'linear-gradient(135deg, #eff6ff 0%, #eef2ff 100%)',
      border: '#60a5fa',
      chipBg: '#dbeafe',
      chipColor: '#1e3a8a',
      actionBg: '#1d4ed8',
      actionText: 'Open Vehicle',
    };
  }
  return { label: 'Reminder', icon: <NotificationsActiveIcon fontSize="small" />, color: 'info' };
}

function safeText(value) {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

function htmlEscape(value) {
  return safeText(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function printFields(item) {
  if (item.type === 'low_stock_part') {
    return [
      ['Part', item.title],
      ['Message', item.message],
      ['Shelf Qty', item.shelf_quantity ?? item.quantity ?? 0],
      ['Shelf Location', item.shelf_location],
      ['Parts Truck Qty', item.parts_truck_quantity ?? 0],
      ['Total Inventory', item.total_inventory_quantity ?? item.quantity ?? 0],
      ['Threshold', item.minimum_quantity ?? 2],
      ['SKU', item.part_sku],
      ['OEM', item.oem_part_number],
      ['Location', item.location],
    ];
  }

  return [
    ['Reminder', item.title],
    ['Vehicle', item.truck_name],
    ['Message', item.message],
    ['Due Date', formatDate(item.due_date)],
    ['Due Odometer', formatKm(item.due_odometer)],
    ['Current Odometer', formatKm(item.current_odometer)],
    ['Priority', item.priority],
    ['Formula', item.due_formula],
  ];
}

function formatPrintDetail(label, value, separator = ': ') {
  if (label === 'OEM') return `<span class="oem-detail">OEM ${htmlEscape(value)}</span>`;
  return `${htmlEscape(label)}${separator}${htmlEscape(value)}`;
}

function previewDetailNodes(fields, itemId) {
  return fields
    .filter(([label]) => !['Part', 'Reminder', 'Message'].includes(label))
    .map(([label, value], index) => (
      <React.Fragment key={`${itemId}-${label}`}>
        {index > 0 && <Box component="span" sx={{ mx: 0.5, color: '#94a3b8' }}>•</Box>}
        {label === 'OEM' ? (
          <Box component="span" sx={{ display: 'inline-block', px: 0.8, py: 0.25, borderRadius: 1, bgcolor: '#001f3f', color: 'white', fontWeight: 950, letterSpacing: 0.8, fontSize: '0.95rem', textTransform: 'uppercase' }}>
            OEM {safeText(value)}
          </Box>
        ) : (
          <Box component="span"><Box component="span" sx={{ fontWeight: 900 }}>{label}:</Box> {safeText(value)}</Box>
        )}
      </React.Fragment>
    ));
}

function buildPrintHtml(title, items) {
  const now = formatPortalDate(new Date());
  const rows = items.map((item, index) => {
    const fields = printFields(item).filter(([, value]) => value !== null && value !== undefined && value !== '');
    const detailText = fields
      .filter(([label]) => !['Part', 'Reminder', 'Message'].includes(label))
      .map(([label, value]) => formatPrintDetail(label, safeText(value)))
      .join(' | ');
    return `
      <tr>
        <td class="num">${index + 1}</td>
        <td><span class="badge">${htmlEscape(typeMeta(item.type).label)}</span></td>
        <td class="strong">${htmlEscape(item.title)}</td>
        <td>${htmlEscape(item.truck_name || item.shelf_location || item.location || '-')}</td>
        <td>${detailText || '-'}</td>
        <td>${htmlEscape(item.message || '-')}</td>
      </tr>
    `;
  }).join('');

  return `<!doctype html>
    <html>
      <head>
        <title>${htmlEscape(title)}</title>
        <style>
          * { box-sizing: border-box; }
          body { margin: 0; padding: 18px; color: #0f172a; font-family: Arial, sans-serif; background: #f8fafc; }
          .sheet { max-width: 1120px; margin: 0 auto; background: white; padding: 18px; border-radius: 14px; box-shadow: 0 10px 26px rgba(15, 23, 42, 0.10); }
          .top { display: flex; justify-content: space-between; gap: 18px; align-items: flex-start; border-bottom: 2px solid #001f3f; padding-bottom: 10px; margin-bottom: 12px; }
          h1 { margin: 0; color: #001f3f; font-size: 22px; }
          .meta { color: #64748b; font-weight: 700; font-size: 12px; text-align: right; }
          .badge { border-radius: 999px; padding: 3px 8px; background: #e5f9eb; color: #166534; font-size: 10px; font-weight: 900; text-transform: uppercase; white-space: nowrap; }
          table { width: 100%; border-collapse: collapse; }
          th, td { text-align: left; vertical-align: top; padding: 6px 7px; border-bottom: 1px solid #e5eaf2; font-size: 11px; }
          th { color: #334155; background: #f8fafc; font-size: 10px; text-transform: uppercase; letter-spacing: 0.35px; }
          .num { width: 34px; color: #64748b; font-weight: 800; }
          .strong { font-weight: 900; color: #001f3f; }
          .oem-detail { display: inline-block; padding: 2px 7px; border-radius: 7px; background: #001f3f; color: white; font-size: 13px; font-weight: 950; letter-spacing: 0.8px; text-transform: uppercase; }
          .no-print { display: flex; gap: 10px; justify-content: flex-end; margin-top: 14px; }
          button { border: 0; border-radius: 10px; padding: 10px 14px; font-weight: 900; cursor: pointer; }
          .primary { background: #001f3f; color: white; }
          .secondary { background: #e2e8f0; color: #0f172a; }
          @media print {
            body { background: white; padding: 0; }
            .sheet { box-shadow: none; border-radius: 0; max-width: none; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <main class="sheet">
          <div class="top">
            <div>
              <h1>${htmlEscape(title)}</h1>
              <div>${items.length} item${items.length === 1 ? '' : 's'}</div>
            </div>
            <div class="meta">BioFleet<br />Generated ${htmlEscape(now)}</div>
          </div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Type</th>
                <th>Title</th>
                <th>Vehicle / Location</th>
                <th>Details</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>${rows || '<tr><td colspan="6">No notifications to print.</td></tr>'}</tbody>
          </table>
          <div class="no-print">
            <button class="secondary" onclick="window.close()">Close</button>
            <button class="primary" onclick="window.print()">Print / Save as PDF</button>
          </div>
        </main>
      </body>
    </html>`;
}

export default function ReminderNotificationBell() {
  const { hasModuleAccess } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogKind, setDialogKind] = useState('reminders');
  const [printPreviewOpen, setPrintPreviewOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const canSeeReminders = hasModuleAccess('dashboard') || hasModuleAccess('vehicles') || hasModuleAccess('service_logs') || hasModuleAccess('parts');

  const purchaseItems = useMemo(() => items.filter((item) => item.type === 'low_stock_part'), [items]);
  const bellItems = useMemo(() => items.filter((item) => item.type !== 'low_stock_part'), [items]);
  const dialogItems = dialogKind === 'purchase' ? purchaseItems : bellItems;
  const dialogIsPurchase = dialogKind === 'purchase';
  const printTitle = dialogIsPurchase ? 'Purchase Notifications' : 'Reminder Notifications';
  const signature = useMemo(() => items.map((item) => item.id).sort().join('|'), [items]);

  const loadReminders = useCallback(async ({ quiet = false } = {}) => {
    if (!canSeeReminders) return;
    try {
      if (!quiet) setLoading(true);
      const res = await notificationService.getReminderNotifications();
      setItems(res.data?.items || []);
    } catch (error) {
      if (!quiet) console.error('Failed to load reminder notifications:', error);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [canSeeReminders]);

  useEffect(() => {
    loadReminders();
    const interval = window.setInterval(() => loadReminders({ quiet: true }), 60000);
    const refresh = () => loadReminders({ quiet: true });
    window.addEventListener('dataUpdated', refresh);
    window.addEventListener('vehiclesUpdated', refresh);
    window.addEventListener('partsUpdated', refresh);
    window.addEventListener('remindersUpdated', refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('dataUpdated', refresh);
      window.removeEventListener('vehiclesUpdated', refresh);
      window.removeEventListener('partsUpdated', refresh);
      window.removeEventListener('remindersUpdated', refresh);
    };
  }, [loadReminders]);

  useEffect(() => {
    if (!signature || items.length === 0) return;
    const lastSeen = sessionStorage.getItem('biofleet.reminderNotificationSignature');
    if (lastSeen !== signature) {
      sessionStorage.setItem('biofleet.reminderNotificationSignature', signature);
      setSnackbarOpen(true);
    }
  }, [items.length, signature]);

  if (!canSeeReminders) return null;

  const openVehicle = (truckId) => {
    if (!truckId) return;
    setDialogOpen(false);
    window.location.hash = `/truck/${truckId}`;
  };

  const openParts = (partId) => {
    setDialogOpen(false);
    const nextHash = partId ? `/inventory?highlightPart=${partId}` : '/inventory';
    const isSameDestination = window.location.hash === `#${nextHash}`;
    window.location.hash = nextHash;
    if (isSameDestination) {
      window.dispatchEvent(new CustomEvent('biofleet:openInventoryPart', { detail: { partId } }));
    }
  };

  const openDialog = (kind) => {
    setDialogKind(kind);
    setDialogOpen(true);
    loadReminders({ quiet: true });
  };

  const printNotifications = () => {
    const printWindow = window.open('', '_blank', 'width=1100,height=800');
    if (!printWindow) return;
    printWindow.document.open();
    printWindow.document.write(buildPrintHtml(printTitle, dialogItems));
    printWindow.document.close();
    printWindow.focus();
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.15, sm: 0.35 } }}>
        <Tooltip title={purchaseItems.length ? `${purchaseItems.length} purchase notification${purchaseItems.length === 1 ? '' : 's'}` : 'No purchase notifications'}>
          <IconButton
            color="inherit"
            onClick={() => openDialog('purchase')}
            sx={{
              color: purchaseItems.length ? '#fed7aa' : 'inherit',
              '&:hover': { bgcolor: 'rgba(249, 115, 22, 0.16)' },
            }}
          >
            <Badge badgeContent={purchaseItems.length} color="warning" max={99}>
              <Inventory2Icon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            </Badge>
          </IconButton>
        </Tooltip>
        <Tooltip title={bellItems.length ? `${bellItems.length} reminder${bellItems.length === 1 ? '' : 's'} due` : 'No reminders due'}>
          <IconButton color="inherit" onClick={() => openDialog('reminders')}>
            <Badge badgeContent={bellItems.length} color="error" max={99}>
            <NotificationsIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            </Badge>
          </IconButton>
        </Tooltip>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={9000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity="warning"
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
          action={(
            <Button color="inherit" size="small" onClick={() => { setSnackbarOpen(false); openDialog(purchaseItems.length ? 'purchase' : 'reminders'); }}>
              View
            </Button>
          )}
          sx={{ borderRadius: 2, fontWeight: 800 }}
        >
          {items.length} notification{items.length === 1 ? '' : 's'} due now
        </Alert>
      </Snackbar>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 'calc(100% - 48px)' },
            height: { xs: '100dvh', sm: 'auto' },
            maxHeight: { xs: '100dvh', sm: '82vh' },
            m: { xs: 0, sm: 3 },
            borderRadius: { xs: 0, sm: 3 },
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 900,
            color: 'white',
            pr: 6,
            py: { xs: 1.5, sm: 2 },
            background: dialogIsPurchase
              ? 'linear-gradient(135deg, #9a3412 0%, #ea580c 100%)'
              : 'linear-gradient(135deg, #001f3f 0%, #075985 100%)',
            boxShadow: dialogIsPurchase
              ? '0 10px 28px rgba(234, 88, 12, 0.24)'
              : '0 10px 28px rgba(0, 31, 63, 0.22)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            {dialogIsPurchase ? (
              <Inventory2Icon sx={{ fontSize: { xs: 22, sm: 26 } }} />
            ) : (
              <NotificationsActiveIcon sx={{ fontSize: { xs: 22, sm: 26 } }} />
            )}
            <Box>
              <Typography sx={{ fontWeight: 950, fontSize: { xs: '1.05rem', sm: '1.35rem' }, lineHeight: 1.05 }}>
                {dialogIsPurchase ? 'Purchase Notifications' : 'Reminder Notifications'}
              </Typography>
              <Typography sx={{ opacity: 0.82, fontSize: { xs: '0.72rem', sm: '0.82rem' }, fontWeight: 700 }}>
                {dialogIsPurchase ? 'Low stock parts that need purchasing' : 'Glove box expiries and maintenance alerts'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setDialogOpen(false)} sx={{ position: 'absolute', right: 12, top: 10, color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, bgcolor: '#f6f8fb' }}>
          {loading ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : dialogItems.length === 0 ? (
            <Box sx={{ py: 6, px: 3, textAlign: 'center' }}>
              {dialogIsPurchase ? (
                <Inventory2Icon sx={{ fontSize: 52, color: '#fb923c', mb: 1, opacity: 0.75 }} />
              ) : (
                <NotificationsIcon sx={{ fontSize: 52, color: '#b0bec5', mb: 1 }} />
              )}
              <Typography sx={{ fontWeight: 900, color: '#001f3f' }}>
                {dialogIsPurchase ? 'No purchase notifications' : 'No reminders due right now'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {dialogIsPurchase ? 'Low-stock purchase alerts will appear here separately.' : 'Glove box and maintenance reminders will pop up here automatically.'}
              </Typography>
            </Box>
          ) : (
            <List disablePadding sx={{ p: { xs: 1, sm: 1.5 } }}>
              {dialogItems.map((item, index) => {
                const meta = typeMeta(item.type);
                const dueDate = formatDate(item.due_date);
                const dueOdometer = formatKm(item.due_odometer);
                const currentOdometer = formatKm(item.current_odometer);
                const isLowStockPart = item.type === 'low_stock_part';
                const isVisuallyDistinct = item.type === 'low_stock_part' || item.type === 'glove_box_expiry';
                return (
                  <React.Fragment key={item.id}>
                    <ListItem
                      alignItems="flex-start"
                      sx={{
                        display: 'block',
                        py: { xs: 1.35, sm: 1.8 },
                        px: { xs: 1.35, sm: 2.2 },
                        pr: { xs: 1.35, sm: 2.2 },
                        mb: index < dialogItems.length - 1 ? { xs: 1, sm: 1.15 } : 0,
                        borderRadius: { xs: 2.25, sm: 3 },
                        bgcolor: 'white',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 8px 22px rgba(15, 23, 42, 0.08)',
                        ...(isVisuallyDistinct ? {
                          bgcolor: meta.bg,
                          border: `1px solid ${meta.border}`,
                          borderLeft: { xs: `5px solid ${meta.accent}`, sm: `8px solid ${meta.accent}` },
                          boxShadow: `0 10px 26px ${meta.accent}18`,
                        } : {}),
                      }}
                    >
                      <ListItemText
                        primary={(
                          <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1.15, flexDirection: { xs: 'column', sm: 'row' } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                              <Chip
                                size="small"
                                icon={meta.icon}
                                label={meta.label}
                                color={isVisuallyDistinct ? undefined : meta.color}
                                sx={{
                                  fontWeight: 900,
                                  flex: '0 0 auto',
                                  ...(isVisuallyDistinct ? {
                                    bgcolor: meta.chipBg,
                                    color: meta.chipColor,
                                    border: `1px solid ${meta.border}`,
                                    '& .MuiChip-icon': { color: meta.chipColor },
                                  } : {}),
                                }}
                              />
                              <Typography sx={{ fontWeight: 950, color: '#001f3f', fontSize: { xs: '0.98rem', sm: '1.05rem' }, lineHeight: 1.22, overflowWrap: 'anywhere' }}>
                                {item.title}
                              </Typography>
                            </Box>
                            {isVisuallyDistinct && (
                              <Box
                                sx={{
                                  ml: { xs: 0, sm: 'auto' },
                                  px: 1,
                                  py: 0.35,
                                  borderRadius: 999,
                                  bgcolor: `${meta.accent}12`,
                                  color: meta.chipColor,
                                  fontSize: '0.72rem',
                                  fontWeight: 900,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.4,
                                }}
                              >
                                {isLowStockPart ? `Shelf Qty: ${item.shelf_quantity ?? item.quantity ?? 0}` : 'Needs attention'}
                              </Box>
                            )}
                          </Box>
                        )}
                        secondary={(
                          <Box component="span" sx={{ display: 'block', mt: 0.8 }}>
                            <Typography component="span" variant="body2" sx={{ display: 'block', color: '#455a64', lineHeight: 1.45, fontSize: { xs: '0.82rem', sm: '0.9rem' } }}>
                              {item.truck_name} — {item.message}
                            </Typography>
                            <Box component="span" sx={{ display: 'flex', gap: { xs: 0.55, sm: 0.8 }, flexWrap: 'wrap', mt: 1, '& .MuiChip-root': { maxWidth: '100%' } }}>
                              {isLowStockPart && <Chip size="small" icon={<Inventory2Icon />} label={`Shelf Qty: ${item.shelf_quantity ?? item.quantity ?? 0}`} color={(item.shelf_quantity ?? item.quantity ?? 0) <= 0 ? 'error' : 'warning'} />}
                              {isLowStockPart && item.shelf_location && <Chip size="small" label={`Shelf Location: ${item.shelf_location}`} sx={{ bgcolor: '#e5f9eb', color: '#166534', border: '1px solid #22c55e55', fontWeight: 900 }} />}
                              {isLowStockPart && <Chip size="small" label={`Parts Truck: ${item.parts_truck_quantity ?? 0}`} />}
                              {isLowStockPart && <Chip size="small" label={`Total Inventory: ${item.total_inventory_quantity ?? item.quantity ?? 0}`} />}
                              {isLowStockPart && <Chip size="small" label={`Threshold: ${item.minimum_quantity ?? 2}`} />}
                              {isLowStockPart && item.part_sku && <Chip size="small" label={`SKU: ${item.part_sku}`} />}
                              {isLowStockPart && item.oem_part_number && <Chip size="small" label={`OEM: ${item.oem_part_number}`} />}
                              {isLowStockPart && item.location && <Chip size="small" label={`Location: ${item.location}`} />}
                              {dueDate && <Chip size="small" icon={<EventIcon />} label={`Due date: ${dueDate}`} />}
                              {dueOdometer && <Chip size="small" icon={<SpeedIcon />} label={`Due at: ${dueOdometer}`} />}
                              {currentOdometer && <Chip size="small" label={`Current: ${currentOdometer}`} />}
                            </Box>
                            {item.due_formula && (
                              <Typography component="span" variant="caption" sx={{ display: 'block', mt: 0.8, color: '#607d8b', fontWeight: 800 }}>
                                {item.due_formula}
                              </Typography>
                            )}
                            <Button
                              fullWidth
                              variant={isVisuallyDistinct ? 'contained' : 'outlined'}
                              size="small"
                              onClick={() => (isLowStockPart ? openParts(item.part_id || item.source_id) : openVehicle(item.truck_id))}
                              sx={{
                                mt: { xs: 1.25, sm: 1.4 },
                                width: { xs: '100%', sm: 'auto' },
                                minWidth: { sm: 150 },
                                fontWeight: 950,
                                borderRadius: 2,
                                ...(isVisuallyDistinct ? {
                                  bgcolor: meta.actionBg,
                                  color: 'white',
                                  boxShadow: `0 8px 20px ${meta.accent}30`,
                                  '&:hover': { bgcolor: meta.actionBg, filter: 'brightness(0.94)' },
                                } : {}),
                              }}
                            >
                              {meta.actionText || (isLowStockPart ? 'Open Inventory' : 'Open Vehicle')}
                            </Button>
                          </Box>
                        )}
                      />
                    </ListItem>
                    {index < dialogItems.length - 1 && <Divider sx={{ display: 'none' }} />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.25, sm: 2 }, bgcolor: 'white', gap: 1, flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
          <Button onClick={() => loadReminders()} disabled={loading} sx={{ width: { xs: '100%', sm: 'auto' }, fontWeight: 900 }}>
            Refresh
          </Button>
          <Button
            startIcon={<PrintIcon />}
            onClick={() => setPrintPreviewOpen(true)}
            disabled={loading || dialogItems.length === 0}
            sx={{ width: { xs: '100%', sm: 'auto' }, fontWeight: 900 }}
          >
            Print Preview
          </Button>
          <Button variant="contained" onClick={() => setDialogOpen(false)} sx={{ bgcolor: '#001f3f', fontWeight: 900, width: { xs: '100%', sm: 'auto' } }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={printPreviewOpen}
        onClose={() => setPrintPreviewOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 950,
            color: 'white',
            pr: 6,
            background: dialogIsPurchase
              ? 'linear-gradient(135deg, #14532d 0%, #16a34a 100%)'
              : 'linear-gradient(135deg, #001f3f 0%, #075985 100%)',
          }}
        >
          Print Preview — {printTitle}
          <IconButton onClick={() => setPrintPreviewOpen(false)} sx={{ position: 'absolute', right: 12, top: 10, color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#f8fafc', p: { xs: 1, sm: 1.5 } }}>
          <Paper sx={{ p: { xs: 1.25, sm: 1.75 }, borderRadius: 2.5, border: '1px solid #d8e2ef' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start', pb: 1, mb: 1, borderBottom: '2px solid #001f3f' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 950, color: '#001f3f', lineHeight: 1.1 }}>{printTitle}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                  {dialogItems.length} item{dialogItems.length === 1 ? '' : 's'} ready to print
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textAlign: 'right' }}>
                BioFleet<br />Generated {formatPortalDate(new Date())}
              </Typography>
            </Box>

            <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: '42px 130px minmax(170px, 1.1fr) minmax(130px, 0.8fr) minmax(240px, 1.5fr) minmax(180px, 1fr)', bgcolor: '#f8fafc', border: '1px solid #e5eaf2', borderBottom: 0 }}>
              {['#', 'Type', 'Title', 'Vehicle / Location', 'Details', 'Message'].map((header) => (
                <Typography key={header} sx={{ p: 0.85, fontSize: '0.68rem', fontWeight: 950, color: '#334155', textTransform: 'uppercase', letterSpacing: 0.4 }}>{header}</Typography>
              ))}
            </Box>

            {dialogItems.map((item, index) => {
              const meta = typeMeta(item.type);
              const fields = printFields(item).filter(([, value]) => value !== null && value !== undefined && value !== '');
              const detailNodes = previewDetailNodes(fields, item.id);
              return (
                <Box
                  key={item.id}
                  sx={{
                    display: { xs: 'block', md: 'grid' },
                    gridTemplateColumns: '42px 130px minmax(170px, 1.1fr) minmax(130px, 0.8fr) minmax(240px, 1.5fr) minmax(180px, 1fr)',
                    bgcolor: 'white',
                    border: '1px solid #e5eaf2',
                    borderLeft: `5px solid ${item.type === 'low_stock_part' ? '#15803d' : '#075985'}`,
                    borderRadius: { xs: 1.5, md: 0 },
                    mb: { xs: 1, md: 0 },
                    overflow: 'hidden',
                    '& > *': { p: 0.85, borderBottom: { xs: '1px solid #eef2f7', md: 0 }, fontSize: '0.78rem' },
                  }}
                >
                  <Typography sx={{ color: '#64748b', fontWeight: 900 }}>{index + 1}</Typography>
                  <Box><Chip size="small" label={meta.label} sx={{ height: 22, fontSize: '0.65rem', fontWeight: 900, bgcolor: item.type === 'low_stock_part' ? '#e5f9eb' : '#e0f2fe', color: item.type === 'low_stock_part' ? '#166534' : '#075985' }} /></Box>
                  <Typography sx={{ fontWeight: 950, color: '#001f3f', overflowWrap: 'anywhere' }}>{item.title}</Typography>
                  <Typography sx={{ fontWeight: 800, color: '#334155', overflowWrap: 'anywhere' }}>{safeText(item.truck_name || item.shelf_location || item.location)}</Typography>
                  <Typography component="div" sx={{ color: '#475569', overflowWrap: 'anywhere' }}>{detailNodes.length ? detailNodes : '-'}</Typography>
                  <Typography sx={{ color: '#475569', overflowWrap: 'anywhere' }}>{safeText(item.message)}</Typography>
                </Box>
              );
            })}
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
          <Button onClick={() => setPrintPreviewOpen(false)} sx={{ width: { xs: '100%', sm: 'auto' }, fontWeight: 900 }}>Close Preview</Button>
          <Button startIcon={<PrintIcon />} variant="contained" onClick={printNotifications} sx={{ width: { xs: '100%', sm: 'auto' }, bgcolor: '#001f3f', fontWeight: 900 }}>
            Print / Save as PDF
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
